"use client";

import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTrigger } from "@/components/ui/sheet";
import { useDebounce } from "@/src/hooks/useDebounce";
import { ConvertLink } from "@/src/tools";
import { RowSearchType, SearchType } from "@/src/types/search";
import { Loader2, Search as SearchIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { FC, useEffect, useState } from "react";

const BASE_URL = process.env.BASE_URL;
const IMAGE_URL = process.env.IMAGE_URL;

const SearchHeader: FC = () => {
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounce(query, 500);
    const [results, setResults] = useState<RowSearchType[]>([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (debouncedQuery.length <= 2) {
            setResults([]);
            return;
        }

        const controller = new AbortController();
        const fetchResults = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${BASE_URL}/search-do-search/${debouncedQuery}`, {
                    signal: controller.signal,
                });
                const data: SearchType = await res.json();
                setResults(data.data.rows);
            } catch (err) {
                if ((err as any).name !== "AbortError") {
                    setResults([]);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
        return () => controller.abort();
    }, [debouncedQuery]);

    const clear = () => {
        setQuery("");
        setResults([]);
    };

    return (
        <Sheet onOpenChange={clear}>
            <SheetTrigger
                asChild
                className="relative h-8 w-8 flex items-center justify-center rounded p-1 hover:bg-gray-100 dark:hover:bg-slate-600 transition"
            >
                <SearchIcon className="w-6 h-6" />
            </SheetTrigger>

            <SheetContent side="left" className="w-[300px] p-0 dark:bg-slate-800">
                <SheetHeader className="p-2 mt-8">
                    <div className="relative">
                        <Input
                            placeholder="نام کالای مورد نظر را وارد کنید"
                            value={query}
                            onChange={(e) => setQuery(e.currentTarget.value)}
                            className="pl-10"
                        />
                        <div className="absolute inset-y-0 left-3 flex items-center">
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                            ) : (
                                <SearchIcon className="w-5 h-5 text-gray-400" />
                            )}
                        </div>
                    </div>
                </SheetHeader>

                <div className="max-h-[70vh] overflow-y-auto">
                    {results.length > 0 ? (
                        results.map((item) => (
                            <Link
                                href={`/product/${ConvertLink(item.product_url)}`}
                                key={item.product_url}
                                className="flex items-center gap-3 p-2 hover:bg-gray-100 dark:hover:bg-slate-700 transition"
                            >
                                <div className="flex-shrink-0 w-12 h-12 relative rounded overflow-hidden bg-gray-100">
                                    <Image
                                        src={IMAGE_URL + item.product_image}
                                        alt={item.product_title}
                                        fill
                                        sizes="48px"
                                        className="object-cover"
                                    />
                                </div>
                                <p className="text-sm line-clamp-2">{item.product_title}</p>
                            </Link>
                        ))
                    ) : (
                        !loading &&
                        debouncedQuery.length > 3 && (
                            <p className="p-4 text-center text-sm text-gray-500">
                                نتیجه‌ای یافت نشد.
                            </p>
                        )
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default SearchHeader;
